import { PaymentMethodAvailabilityStatus, StripeSetupIntentStatus } from '@qdrant/qdrant-cloud-public-api/payment/v1';
import { type QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { paymentService } from '@/api/services/payment';
import { reportError } from '@/helpers/errors/report';

/**
 * Stripe redirects to the same page with the session_id in the URL.
 * Anytime we get a successful session with succeeded payment intent we must store the payment method.
 * It removes the session_id from the URL and redirects to the same page.
 * @see https://github.com/qdrant/qdrant-cloud-cluster-api/blob/af86f601965d190a20944286390ff742fd7d2cbf/cluster_api/booking/payment/stripe_/service_fn.py#L173
 */
export async function resolvePaymentSession({
  queryClient,
  accountId,
  sessionId,
}: {
  queryClient: QueryClient;
  accountId: string;
  sessionId: string;
}): Promise<never> {
  const getStripeCheckoutSession = paymentService.getStripeCheckoutSessionQuery({ accountId, sessionId });
  const data = await queryClient.fetchQuery({ ...getStripeCheckoutSession, staleTime: 0, gcTime: 0 });

  if (data.stripeSession?.setupIntentStatus !== StripeSetupIntentStatus.SUCCEEDED) {
    // Keep the message static (ids in `extra`) so all setup failures group into a single Sentry issue
    // instead of one per account/session.
    reportError(new Error('Stripe setup failed'), {
      domain: 'billing',
      action: 'resolve-payment-session',
      level: 'fatal',
      extra: { accountId, sessionId },
    });
  }

  throw redirect({
    to: '.',
    search: (prev) => ({ ...prev, session_id: undefined }),
    hash: true,
    replace: true,
  });
}

export async function resolvePaymentMethodAvailable({
  queryClient,
  accountId,
}: {
  queryClient: QueryClient;
  accountId: string;
}): Promise<boolean> {
  const data = await queryClient.fetchQuery({
    ...paymentService.getPaymentMethodAvailabilityQuery({ accountId }),
    staleTime: 0,
  });

  return data.availability?.status === PaymentMethodAvailabilityStatus.AVAILABLE;
}
